import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import { getFirestore, collection, query, where, CollectionReference, Query } from 'firebase/firestore';
import { User } from 'firebase/auth';
import { Pool, EnvironmentDocument, RolloutDocument } from '@finvari/deliver';
import { useSubscribedList } from '../utils/firebaseHooks';
import { useConfiguredEnvironments } from './EnvironmentsContext';

// Serves the `pools` collection alongside the live queue (queued rollouts), and joins pools to their
// member environments so list/detail views can render groups without extra reads.
interface PoolsContextType {
  pools: Pool[];
  poolsById: Record<string, Pool>;
  // Member environments for each pool (keyed by poolId), drawn from EnvironmentsContext.
  membersByPoolId: Record<string, EnvironmentDocument[]>;
  // Queued (not-yet-assigned) rollouts across all pools, and grouped by pool for queue-depth display.
  queuedRollouts: RolloutDocument[];
  queuedByPoolId: Record<string, RolloutDocument[]>;
  loading: boolean;
  error: string | null;
}

const PoolsContext = createContext<PoolsContextType | undefined>(undefined);

export const usePools = (): PoolsContextType => {
  const context = useContext(PoolsContext);
  if (context === undefined) {
    throw new Error('usePools must be used within a PoolsProvider');
  }
  return context;
};

interface PoolsProviderProps {
  children: ReactNode;
  user: User | null;
}

export const PoolsProvider: React.FC<PoolsProviderProps> = ({ children, user }) => {
  const poolsQuery = useMemo(() => {
    if (!user) return undefined;
    const db = getFirestore();
    return collection(db, 'pools') as CollectionReference<Pool>;
  }, [user]);

  const queuedQuery = useMemo(() => {
    if (!user) return undefined;
    const db = getFirestore();
    return query(collection(db, 'rollouts'), where('status', '==', 'queued')) as Query<RolloutDocument>;
  }, [user]);

  // Every source this context joins contributes to loading/error, so a failed queue or
  // environments subscription surfaces instead of rendering as an empty queue / target list.
  const { pools, loading: poolsLoading, error: poolsError } = useSubscribedList<Pool, 'pools'>(poolsQuery, 'pools');
  const {
    queuedRollouts,
    loading: queueLoading,
    error: queueError,
  } = useSubscribedList<RolloutDocument, 'queuedRollouts'>(queuedQuery, 'queuedRollouts');
  const { environments, loading: environmentsLoading, error: environmentsError } = useConfiguredEnvironments();

  const loading = poolsLoading || queueLoading || environmentsLoading;
  const error = poolsError ?? queueError ?? environmentsError;

  const poolsById = useMemo(() => {
    const byId: Record<string, Pool> = {};
    for (const pool of pools) byId[pool.poolId] = pool;
    return byId;
  }, [pools]);

  const membersByPoolId = useMemo(() => {
    const byPool: Record<string, EnvironmentDocument[]> = {};
    for (const env of environments) {
      if (!env.poolId) continue;
      (byPool[env.poolId] ??= []).push(env);
    }
    return byPool;
  }, [environments]);

  const queuedByPoolId = useMemo(() => {
    const byPool: Record<string, RolloutDocument[]> = {};
    for (const rollout of queuedRollouts) {
      if (!rollout.poolId) continue;
      (byPool[rollout.poolId] ??= []).push(rollout);
    }
    // Stable FIFO order for display.
    for (const list of Object.values(byPool)) {
      list.sort((a, b) => String(a.createdAt).localeCompare(String(b.createdAt)));
    }
    return byPool;
  }, [queuedRollouts]);

  const value: PoolsContextType = {
    pools,
    poolsById,
    membersByPoolId,
    queuedRollouts,
    queuedByPoolId,
    loading,
    error,
  };

  return <PoolsContext.Provider value={value}>{children}</PoolsContext.Provider>;
};
